import { Position, Positions } from '@agilox/ui-common';
import {
	ApplicationRef,
	ChangeDetectorRef,
	ComponentRef,
	createComponent,
	Directive,
	ElementRef,
	EmbeddedViewRef,
	EnvironmentInjector,
	HostListener,
	inject,
	Injector,
	input,
	model,
	OnDestroy,
	Renderer2,
} from '@angular/core';
import { TooltipComponent } from '../component/tooltip/tooltip.component';

@Directive({
	selector: '[uiTooltip]',
	standalone: false,
})
export class TooltipDirective implements OnDestroy {
	uiTooltip = input<string>('');
	tooltipVisible = input<boolean>(true);
	tooltipPosition = model<Position>(Positions.Top);
	tooltipStyleClass = input<string>('');
	tooltipOnMobile = input<boolean>(false);

	private _initialTooltipPosition: Position = Positions.Top;

	public componentRef: ComponentRef<any> | null = null;

	private _isTouchEvent: boolean = false;

	private elementRef = inject(ElementRef);
	private appRef = inject(ApplicationRef);
	private environmentInjector = inject(EnvironmentInjector);
	private injector = inject(Injector);
	private renderer = inject(Renderer2);

	private readonly minimalSpace = 8;

	@HostListener('mouseenter')
	onMouseEnter(): void {
		this.initializeTooltip();
	}

	@HostListener('mouseleave')
	onMouseLeave(): void {
		this.destroy();
	}

	@HostListener('document:click')
	onMouseClick(): void {
		if (this._isTouchEvent) {
			this._isTouchEvent = false;
			return;
		}
		this.destroy();
	}

	@HostListener('touchstart')
	onTouchStart(): void {
		if (!this.tooltipOnMobile()) {
			return;
		}

		this._isTouchEvent = true;
		this.disableTextSelection();
		this.initializeTooltip();
	}

	@HostListener('focus')
	onFocus(): void {
		this.initializeTooltip();
	}

	@HostListener('blur')
	onBlur(): void {
		this.destroy();
	}

	@HostListener('mousedown')
	onMouseDown(): void {
		this.destroy();
	}

	private initializeTooltip() {
		if (this.componentRef === null && this.tooltipVisible() && this.uiTooltip()) {
			this.componentRef = createComponent(TooltipComponent, {
				environmentInjector: this.environmentInjector,
				elementInjector: this.injector,
			});

			this.appRef.attachView(this.componentRef.hostView);
			const [tooltipDOMElement] = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes;
			this.setTooltipComponentProperties();
			document.body.appendChild(tooltipDOMElement);
			this._initialTooltipPosition = this.tooltipPosition();

			requestAnimationFrame(() => this.showTooltip());
		}
	}

	private setTooltipComponentProperties() {
		if (this.componentRef !== null) {
			this.componentRef.instance.tooltip.set(this.uiTooltip());
			this.componentRef.instance.position.set(this.tooltipPosition());
			this.componentRef.instance.styleClass.set(this.tooltipStyleClass());

			const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();

			switch (this.tooltipPosition()) {
				case Positions.Bottom: {
					this.componentRef.instance.left.set(Math.round((right - left) / 2 + left));
					this.componentRef.instance.top.set(Math.round(bottom));

					this.componentRef.instance.caretX.set(Math.round((right - left) / 2 + left));
					this.componentRef.instance.caretY.set(Math.round(bottom));
					break;
				}
				case Positions.Top: {
					this.componentRef.instance.left.set(Math.round((right - left) / 2 + left));
					this.componentRef.instance.top.set(Math.round(top));

					this.componentRef.instance.caretX.set(Math.round((right - left) / 2 + left));
					this.componentRef.instance.caretY.set(Math.round(top));
					break;
				}
				case Positions.Right: {
					this.componentRef.instance.left.set(Math.round(right));
					this.componentRef.instance.top.set(Math.round(top + (bottom - top) / 2));

					this.componentRef.instance.caretX.set(Math.round(right));
					this.componentRef.instance.caretY.set(Math.round(top + (bottom - top) / 2));
					break;
				}
				case Positions.Left: {
					this.componentRef.instance.left.set(Math.round(left));
					this.componentRef.instance.top.set(Math.round(top + (bottom - top) / 2));

					this.componentRef.instance.caretX.set(Math.round(left));
					this.componentRef.instance.caretY.set(Math.round(top + (bottom - top) / 2));
					break;
				}
			}
		}
	}

	private showTooltip() {
		if (this.componentRef !== null) {
			this.componentRef.instance.visible.set(true);
			this.componentRef.injector.get(ChangeDetectorRef).markForCheck();
			requestAnimationFrame(() => this.adjustPosition());
		}
	}

	/**
	 * If the position is set to TOP and the tooltip exceeds the viewport, the position is adjusted to BOTTOM
	 * If the position is set to BOTTOM and the tooltip exceeds the viewport, the position is adjusted to TOP
	 */
	private adjustPosition() {
		if (!this.componentRef?.instance.tooltipElement()) {
			return;
		}

		const tooltipBox = this.componentRef.instance.tooltipContent().nativeElement;
		const { left, right, top, bottom } = tooltipBox.getBoundingClientRect();
		switch (this.tooltipPosition()) {
			case Positions.Left:
				if (left < this.minimalSpace) {
					this.tooltipPosition.set(Positions.Right);
					this.setTooltipComponentProperties();
				}
				break;
			case Positions.Right:
				if (right > window.innerWidth - this.minimalSpace) {
					this.tooltipPosition.set(Positions.Left);
					this.setTooltipComponentProperties();
				}
				break;
			case Positions.Top:
				if (top < this.minimalSpace) {
					this.tooltipPosition.set(Positions.Bottom);
					this.setTooltipComponentProperties();
				}
				break;
			case Positions.Bottom:
				if (bottom > window.innerHeight - this.minimalSpace) {
					this.tooltipPosition.set(Positions.Top);
					this.setTooltipComponentProperties();
				}
				break;
		}

		const adjustedRect = this.componentRef.instance
			.tooltipContent()
			.nativeElement.getBoundingClientRect();
		const { left: newLeft, top: newTop, width: newWidth, height: newHeight } = adjustedRect;

		switch (this.tooltipPosition()) {
			case Positions.Left:
			case Positions.Right:
				this.componentRef.instance.top.set(Math.round(newTop - newHeight / 2));
				if (this.componentRef.instance.top() < this.minimalSpace) {
					this.componentRef.instance.top.set(this.minimalSpace);
				} else if (
					this.componentRef.instance.top() + newHeight >
					window.innerHeight - this.minimalSpace
				) {
					this.componentRef.instance.top.set(window.innerHeight - newHeight - this.minimalSpace);
				}
				break;
			case Positions.Top:
			case Positions.Bottom:
				this.componentRef.instance.left.set(Math.round(newLeft - newWidth / 2));
				if (this.componentRef.instance.left() < this.minimalSpace) {
					this.componentRef.instance.left.set(this.minimalSpace);
				} else if (
					this.componentRef.instance.left() + newWidth >
					window.innerWidth - this.minimalSpace
				) {
					this.componentRef.instance.left.set(window.innerWidth - newWidth - this.minimalSpace);
				}
				break;
		}
	}

	ngOnDestroy(): void {
		this.destroy();
	}

	destroy(): void {
		if (this.componentRef !== null) {
			this.appRef.detachView(this.componentRef.hostView);
			this.componentRef.destroy();
			this.componentRef = null;
			this.tooltipPosition.set(this._initialTooltipPosition);
		}
	}

	/*
  Handles  cases where a prolonged touch event triggers text election of the touched element
   */
	private disableTextSelection() {
		this.renderer.setStyle(this.elementRef.nativeElement, '-webkit-user-select', 'none');
		this.renderer.setStyle(this.elementRef.nativeElement, '-moz-user-select', 'none');
		this.renderer.setStyle(this.elementRef.nativeElement, '-ms-user-select', 'none');
		this.renderer.setStyle(this.elementRef.nativeElement, 'user-select', 'none');
	}
}
